import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxSquadCard from './ArenaxSquadCard';
import { Plus, Filter, Users, Search } from 'lucide-react';

const SPORTS = ['Football','Cricket','Badminton','Tennis','Basketball','Pickleball','Volleyball','Table Tennis'];

export default function ArenaxSquads() {
  const [params, setParams] = useSearchParams();
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get('q') || '');
  const sport = params.get('sport') || '';
  const city = params.get('city') || '';

  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (sport) p.set('sport', sport);
    if (city) p.set('city', city);
    setLoading(true);
    api.get(`/arenax/squads?${p.toString()}`)
      .then(r => setSquads(r.data.squads || []))
      .finally(() => setLoading(false));
  }, [q, sport, city]);

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  return (
    <ArenaxLayout>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="arenax-squads-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
          <div>
            <div className="text-[11px] tracking-[0.4em] text-cyan-300 font-bold mb-2">YOUR TRIBE</div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-100">Find your squad.</h1>
            <p className="text-sm text-slate-400 mt-1">Recurring games. Real teammates. Weekly rituals.</p>
          </div>
          <Link to="/arenax/squads/create" data-testid="arenax-squads-create-cta">
            <button className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-950 flex items-center gap-2"
              style={{ background: 'var(--ax-primary-gradient)' }}>
              <Plus className="h-4 w-4" />Create Squad
            </button>
          </Link>
        </div>

        {/* Search + filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={e => setQ(e.target.value)}
              data-testid="arenax-squads-search"
              placeholder="Search by squad or city..."
              className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900/60 border border-slate-700 focus:border-cyan-500 focus:outline-none text-sm text-slate-100 placeholder:text-slate-600" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-7">
          <Filter className="h-3.5 w-3.5 text-slate-500" />
          <FilterChip label="All sports" active={!sport} onClick={() => setFilter('sport', '')} />
          {SPORTS.map(s => <FilterChip key={s} label={s} active={sport === s} onClick={() => setFilter('sport', s)} />)}
        </div>

        {loading ? (
          <div className="text-center text-slate-500 text-sm py-12">Loading squads...</div>
        ) : squads.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-cyan-500/20 p-14 text-center" data-testid="arenax-squads-empty">
            <Users className="h-10 w-10 text-cyan-400/50 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-200 mb-2">Your city's first squad starts with you.</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
              Squads create weekly rituals — same crew, same ground, same time. The first squad in a sport sets the rhythm for the whole city.
            </p>
            <Link to="/arenax/squads/create" data-testid="arenax-squads-empty-create">
              <button className="px-6 py-3 rounded-xl text-sm font-bold text-slate-950 inline-flex items-center gap-2"
                style={{ background: 'var(--ax-primary-gradient)' }}>
                Create the First Squad
              </button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="arenax-squads-grid">
            {squads.map(s => <ArenaxSquadCard key={s.id} squad={s} />)}
          </div>
        )}
      </section>
    </ArenaxLayout>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all capitalize ${
        active ? 'bg-cyan-500 text-slate-950 shadow-md' : 'bg-slate-900 text-slate-300 border border-slate-700 hover:border-cyan-500/50'
      }`}>{label}</button>
  );
}
